import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import DistributorCardChargeback from './DistributorCardChargeback';
import DistributorPaymentSupportCase from './DistributorPaymentSupportCase';
import DistributorMerchantFunding from './DistributorMerchantFunding';
import { moneyMinor } from '../../utils/money';
import {
  readPurchaseReceipt,
  purchaseState,
  purchaseCreditMessage,
  samePurchaseQuote,
} from '../distributor/distributorPurchaseReceipt';

const unwrap = (response) => response?.data?.data;
const date = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const safeCount = (value) => Number.isSafeInteger(value) && value >= 0;
const emptyReview = { purchaseId: '', expectedRevision: 0, reason: '', confirmedReconciled: false };

function readRecovery(value) {
  const receipt = readPurchaseReceipt({ data: { data: value } });
  const recovery = value.recovery;
  if (
    receipt.method !== 'card' ||
    !recovery ||
    !safeCount(recovery.revision) ||
    typeof recovery.blocked !== 'boolean' ||
    typeof recovery.canResolve !== 'boolean' ||
    typeof recovery.canRecordChargeback !== 'boolean' ||
    !(
      recovery.paymentReference === null ||
      (typeof recovery.paymentReference === 'string' &&
        /^[A-Za-z0-9._:-]{1,200}$/.test(recovery.paymentReference))
    ) ||
    !(recovery.resolvedAt === null || date(recovery.resolvedAt)) ||
    (recovery.canResolve &&
      (!recovery.blocked || !receipt.cardAttempt || recovery.resolvedAt !== null))
  )
    throw new Error('Invalid Card review');
  return receipt;
}

/** Admin review does not move money, alter the ledger or initiate/retry any payment. */
export default function DistributorCardRecovery({
  distributorId,
  onRefresh,
  onFundingPendingChange,
  fundingDisabled,
}) {
  const t = useT();
  const [queue, setQueue] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [review, setReview] = useState(emptyReview);
  const [intent, setIntent] = useState(null);
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const intentRef = useRef(null);
  const busyRef = useRef(false);
  const originalSession = useRef(captureSession());
  const originalScope = useRef(shippingAccessScope());
  const current = () =>
    isSessionCurrent(originalSession.current) && originalScope.current === shippingAccessScope();
  const refreshCallback = useRef(onRefresh);
  refreshCallback.current = onRefresh;
  const { begin, version } = useShippingRequest(distributorId);
  const load = useCallback(
    async (nextPage = 1) => {
      if (busyRef.current) return;
      const request = begin();
      setLoading(true);
      setQueue(null);
      setReview(emptyReview);
      setError('');
      try {
        const value = unwrap(
          await distributorService.adminCardRecovery(distributorId, nextPage, {
            signal: request.signal,
          })
        );
        if (!request.isCurrent()) return;
        if (
          !Array.isArray(value?.data) ||
          value.data.length > 20 ||
          !safeCount(value.total) ||
          value.page !== nextPage ||
          value.limit !== 20 ||
          value.totalPages !== Math.ceil(value.total / 20) ||
          new Set(value.data.map((row) => row?._id)).size !== value.data.length
        )
          throw new Error('Invalid Card queue');
        const data = value.data.map(readRecovery);
        if (nextPage > Math.max(1, value.totalPages)) {
          busyRef.current = false;
          await load(Math.max(1, value.totalPages));
          return;
        }
        setQueue({ ...value, data });
        setPage(nextPage);
        // A lost write remains locked. A queue revision cannot prove which request resolved it.
        if (intentRef.current)
          setError(
            'Review result is unconfirmed. Retry the same review request to recover its receipt.'
          );
      } catch {
        if (request.isCurrent())
          setError('Card review records are unavailable. Reload before continuing.');
      } finally {
        if (request.isCurrent()) setLoading(false);
      }
    },
    [begin, distributorId]
  );
  useEffect(() => {
    busyRef.current = false;
    setBusy(false);
    intentRef.current = null;
    setIntent(null);
    setPendingCaseId(null);
    setReview(emptyReview);
    setNotice('');
    load(1);
  }, [load, version]);
  const submit = async (event) => {
    event.preventDefault();
    if (!current() || busyRef.current || loading || pendingCaseId) return;
    const row = queue?.data.find((value) => value._id === review.purchaseId);
    let captured = intentRef.current;
    if (!captured) {
      if (
        !row?.recovery.canResolve ||
        row.recovery.revision !== review.expectedRevision ||
        !review.confirmedReconciled ||
        review.reason.trim().length < 12 ||
        review.reason.trim().length > 300
      )
        return;
      const requestKey = window.crypto?.randomUUID?.();
      if (!requestKey) {
        setError('A secure request key is unavailable. Reload before reviewing.');
        return;
      }
      captured = {
        purchaseId: row._id,
        quote: row,
        body: {
          requestKey,
          expectedRevision: review.expectedRevision,
          reason: review.reason.trim(),
          confirmedReconciled: true,
        },
      };
      intentRef.current = captured;
      setIntent(captured);
    }
    busyRef.current = true;
    const request = begin();
    if (!request.isCurrent()) {
      busyRef.current = false;
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const receipt = readRecovery(
        unwrap(
          await distributorService.reconcileCardReturn(
            distributorId,
            captured.purchaseId,
            captured.body
          )
        )
      );
      if (!request.isCurrent()) return;
      const action = receipt.resolutionReceipt;
      if (
        receipt._id !== captured.purchaseId ||
        !samePurchaseQuote(receipt, captured.quote) ||
        receipt.status !== captured.quote.status ||
        receipt.bankEntryId !== captured.quote.bankEntryId ||
        receipt.creditedAt !== captured.quote.creditedAt ||
        action?.requestKey !== captured.body.requestKey ||
        action.expectedRevision !== captured.body.expectedRevision ||
        action.resolvedRevision !== captured.body.expectedRevision + 1 ||
        !date(action.resolvedAt) ||
        Date.parse(action.resolvedAt) < Date.parse(receipt.createdAt) ||
        receipt.recovery.revision < action.resolvedRevision ||
        (receipt.recovery.revision === action.resolvedRevision &&
          (receipt.recovery.canResolve || receipt.recovery.resolvedAt !== action.resolvedAt))
      )
        throw new Error('Invalid Card review receipt');
      intentRef.current = null;
      setIntent(null);
      setReview(emptyReview);
      setNotice(
        receipt.recovery.canResolve
          ? 'Earlier reconciliation recovered. A newer return review remains open. Family spending remains paused. No units or money changed.'
          : receipt.recovery.blocked
            ? 'Reconciliation recorded. Family spending remains paused for another return review. No units or money changed.'
            : 'Reconciliation recorded. This review changed no units or money.'
      );
      refreshCallback.current?.();
      busyRef.current = false;
      setBusy(false);
      await load(page);
    } catch {
      if (request.isCurrent())
        setError(
          'Review result is unconfirmed. Retry the same review request to recover its receipt.'
        );
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  return (
    <section className="me-distributor-admin-card space-y-3" aria-label={t('Card payment review')}>
      <h2 className="text-lg font-semibold">{t('Card payment review')}</h2>
      <p>
        {t(
          'Review merchant funding, refunds and returns. These confirmations do not credit units or move money; payment and accounting checks remain separate.'
        )}
      </p>
      <button
        type="button"
        className="me-dw-link"
        disabled={busy || loading || Boolean(pendingCaseId)}
        onClick={() => load(page)}
      >
        {t('Refresh Card reviews')}
      </button>
      {loading && <p role="status">{t('Loading Card reviews...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {queue?.data.length === 0 && (
        <p>{t('No Card attempts need observation or return review.')}</p>
      )}
      {queue && (
        <p>
          {t(
            'Showing {count} Card records. Funding without a linked bank entry is not a unit credit.',
            { count: queue.total }
          )}
        </p>
      )}
      {queue?.data.map((row) => (
        <div key={row._id} className="rounded border p-3 text-sm">
          <p>
            {row.familyKey.replaceAll('_', ' ')} · {moneyMinor(row.amountCents)} USD · {row._id}
          </p>
          <p>{t(purchaseState(row))}</p>
          <p>{t(purchaseCreditMessage(row))}</p>
          {!intent && row.cardAttempt?.state === 'funded' && (
            <DistributorMerchantFunding
              distributorId={distributorId}
              purchase={row}
              disabled={
                fundingDisabled ||
                busy ||
                loading ||
                Boolean(pendingCaseId && pendingCaseId !== row._id)
              }
              onPendingChange={(value) => {
                setPendingCaseId(value ? row._id : null);
                onFundingPendingChange?.(value);
              }}
              onRefresh={() => refreshCallback.current?.()}
            />
          )}
          {row.bankEntryId && (
            <p>
              {t('Bank entry ID')}: {row.bankEntryId}
            </p>
          )}
          <p>
            {t(
              row.recovery.blocked
                ? 'Family spending paused; affected unsent orders remain paused.'
                : row.recovery.resolvedAt
                  ? 'Return reconciliation recorded.'
                  : 'No return restriction recorded.'
            )}
          </p>
          {!intent && row.recovery.canViewSupportCase === true && (
            <DistributorPaymentSupportCase
              distributorId={distributorId}
              purchase={row}
              disabled={
                busy || loading || Boolean(pendingCaseId && pendingCaseId !== `support:${row._id}`)
              }
              onPendingChange={(value) => setPendingCaseId(value ? `support:${row._id}` : null)}
            />
          )}
          {row.recovery.canRecordChargeback && !intent && (
            <DistributorCardChargeback
              key={`${row._id}:${row.recovery.revision}`}
              distributorId={distributorId}
              purchase={row}
              disabled={busy || loading || Boolean(pendingCaseId && pendingCaseId !== row._id)}
              onPendingChange={(value) => setPendingCaseId(value ? row._id : null)}
              onRecorded={() => {
                refreshCallback.current?.();
                load(page);
              }}
            />
          )}
          {row.recovery.canResolve && (
            <button
              type="button"
              className="me-dw-link"
              disabled={busy || Boolean(intent) || Boolean(pendingCaseId)}
              onClick={() => {
                if (!current()) return;
                const request = begin();
                if (request.isCurrent())
                  setReview({
                    ...emptyReview,
                    purchaseId: row._id,
                    expectedRevision: row.recovery.revision,
                  });
              }}
            >
              {t('Review card purchase {id}', { id: row._id })}
            </button>
          )}
        </div>
      ))}
      {(review.purchaseId || intent) && (
        <form onSubmit={submit} className="space-y-3">
          <p>
            {t('Purchase ID')}: {intent?.purchaseId || review.purchaseId}
          </p>
          <fieldset disabled={busy || Boolean(intent)} className="space-y-3">
            <label className="block">
              {t('Reconciliation reason')}
              <textarea
                className="block w-full rounded border p-2"
                maxLength={300}
                value={review.reason}
                onChange={(event) =>
                  setReview((value) => ({
                    ...value,
                    reason: event.target.value,
                    confirmedReconciled: false,
                  }))
                }
              />
            </label>
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={review.confirmedReconciled}
                onChange={(event) =>
                  setReview((value) => ({ ...value, confirmedReconciled: event.target.checked }))
                }
              />
              {t(
                'I reviewed bank, payment and accounting reconciliation and authorize resolving this payment review.'
              )}
            </label>
          </fieldset>
          <button
            type="submit"
            className="me-dw-primary"
            disabled={
              busy ||
              loading ||
              Boolean(pendingCaseId) ||
              (!intent && (!review.confirmedReconciled || review.reason.trim().length < 12))
            }
          >
            {t(
              intent ? 'Recover same reconciliation request' : 'Record card payment reconciliation'
            )}
          </button>
        </form>
      )}
      {queue?.totalPages > 1 && (
        <nav aria-label={t('Card review pages')} className="flex gap-3">
          <button
            type="button"
            className="me-dw-link"
            disabled={busy || loading || Boolean(intent) || Boolean(pendingCaseId) || page <= 1}
            onClick={() => load(page - 1)}
          >
            {t('Previous')}
          </button>
          <span>{t('Page {page} of {pages}', { page, pages: queue.totalPages })}</span>
          <button
            type="button"
            className="me-dw-link"
            disabled={
              busy ||
              loading ||
              Boolean(intent) ||
              Boolean(pendingCaseId) ||
              page >= queue.totalPages
            }
            onClick={() => load(page + 1)}
          >
            {t('Next')}
          </button>
        </nav>
      )}
    </section>
  );
}
